import { isUndefined } from 'lodash';
import { getFramework, ResourceThumbnail } from '@/framework';
import { deleteExtraInfo } from '@/utils/layer-kit';
import {
    LayerType,
    MediaType,
    type IExportBehavior,
    type ILayerBehavior,
    type ILayerOriginInfo,
    type ILayerProps,
    type IMediaLayerProps,
    type ISize,
} from '@/version-types';
import { EdgelessMediaLayer } from './edgeless';
import { EdgelessGIFElement } from './edgeless/edgeless-gif-element';
import { EditableVideoOperate, PlayVideoOperate } from './operate/play-video-operate';
import { renderLayerContentToCanvas } from './render/render-to-canvas';
import { renderLayerToPPT } from './render/render-to-ppt';
import { renderLayerToStage } from './render/render-to-stage';

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessMediaLayer(layer.id, getFramework(identity));
        },

        customActiveOperate(layer) {
            if ((layer.data as IMediaLayerProps).mediaType !== MediaType.Video) return null;
            return <EditableVideoOperate layer={layer} />;
        },

        customHoverOperate(layer: ILayerProps) {
            const framework = getFramework(identity);
            const readonly = framework.service.editor.getReadonly();
            const isEdgelessMode = framework.service.edgelessCanvas.isEdgelessMode;
            if (!isEdgelessMode) return null;

            if ((layer.data as IMediaLayerProps).mediaType === MediaType.Video && readonly) {
                return <PlayVideoOperate layer={layer} />;
            }
            return null;
        },

        async savePPTLayerAsync(slide, layer, _stageWidth, _stageHeight) {
            return renderLayerToPPT(slide, layer, identity);
        },
        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            return renderLayerToStage(layer, identity, resultSize);
        },
        // needScaleCanvas 当原图超过一定大小的时候是否压缩
        async getLayerImageAsync(
            layer: ILayerProps,
            globalAlpha = true,
            needScaleCanvas = true,
            _noNeedBorderRadius = false,
        ) {
            return renderLayerContentToCanvas(layer, identity, globalAlpha, needScaleCanvas);
        },

        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },
        getScaleIsNeedLimitSize(): boolean {
            return true;
        },
        whenGroupCanResize(layer: ILayerProps) {
            const horizontal = true,
                vertical = true;
            return { horizontal, vertical };
        },

        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            deleteExtraInfo(layer);
        },

        onBeforeAddOrUpdate(layer: ILayerProps) {
            if (isUndefined(layer.__renderAiWatermark)) {
                layer.__renderAiWatermark = !!layer.extraInfo?.watermarkCoverUrl || !!layer.extraInfo?.watermarkURL;
            }
        },

        disableDefaultRotateOperate(layer) {
            const framework = getFramework(identity);
            const isEdgelessMode = framework.service.edgelessCanvas.isEdgelessMode;
            const props = layer.data as IMediaLayerProps;
            return isEdgelessMode && props.mediaType === MediaType.Video;
        },

        disableDefaultResizeOperate(layer) {
            const props = layer.data as IMediaLayerProps;
            return props.mediaType === MediaType.Video;
        },

        getMenuOverlapName(layer: ILayerProps) {
            const props = layer.data as IMediaLayerProps;
            if (props.mediaType !== MediaType.Image) return 'Media';
            const object = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id);
            const element = object instanceof EdgelessMediaLayer ? object.mediaElement : undefined;
            const format = element instanceof EdgelessGIFElement ? element.getFormat() : undefined;
            // 优先使用文件内容识别结果，尚未加载时通过资源后缀兜底
            const isWebP = format ? format === 'webp' : /\.webp(?:[?#]|$)/i.test(props.url);
            return isWebP ? 'WebP' : 'Gif';
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            const extraInfo = layer.extraInfo || {};
            const props = layer.data as IMediaLayerProps;
            const fallbackUrl = extraInfo.watermarkCoverUrl || props.poster || props.url;
            return <ResourceThumbnail resource={resource} fallbackUrl={fallbackUrl} />;
        },
    };
    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.MEDIA_LAYER,
    getBehavior,
};

export default exportBehavior;
